import * as React from 'react';
import { useQuery } from 'react-query';
import {
  Activity,
  CheckCircle2,
  Download,
  FileMinus2,
  FilePlus2,
  Loader,
  Pencil,
  Trash2,
  UserCircle,
  XCircle,
} from 'lucide-react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { useViagensBilling } from 'hooks/api/viagens-billing';
import { Formatter } from 'utils';
import type { ViagemBilling, ViagemBillingActivity } from '../types';

type Props = {
  billing: ViagemBilling | null;
  onOpenChange: (open: boolean) => void;
};

const formatDate = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm:ss' }) : '---';
};

type ActivityDataInfo = {
  viagemBillingId?: number | null;
  billingId?: number | null;
  viagemIds?: number[] | null;
  viagens?: Array<{
    viagemControleId?: number | null;
    idViagem?: string | null;
  }> | null;
  totalItems?: number | null;
  justificationDescription?: string | null;
  comment?: string | null;
};

const parseDataInfo = (dataInfo?: string | null): ActivityDataInfo => {
  if (!dataInfo) return {};

  try {
    return JSON.parse(dataInfo);
  } catch {
    return {};
  }
};

const isRemoveFromBillingActivity = (
  activity: ViagemBillingActivity,
  dataInfo: ActivityDataInfo
) => {
  return (
    activity.operation === 'REMOVE_FROM_BILLING' ||
    (activity.operation === 'DELETE' &&
      activity.table === 'viagem_billing_items' &&
      dataInfo.totalItems !== 0)
  );
};

const getActivityPresentation = (
  activity: ViagemBillingActivity,
  dataInfo: ActivityDataInfo
) => {
  if (isRemoveFromBillingActivity(activity, dataInfo)) {
    return {
      title: 'Viagem removida da fatura',
      tone: 'text-amber-700 bg-amber-50 border-amber-200',
      icon: <FileMinus2 className="h-5 w-5" />,
    };
  }

  switch (activity.operation) {
    case 'CREATE':
      return {
        title: 'Fatura criada',
        tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
        icon: <FilePlus2 className="h-5 w-5" />,
      };
    case 'UPDATE':
      return {
        title: 'Fatura atualizada',
        tone: 'text-blue-700 bg-blue-50 border-blue-200',
        icon: <Pencil className="h-5 w-5" />,
      };
    case 'EXPORT':
      return {
        title: 'Fatura exportada',
        tone: 'text-indigo-700 bg-indigo-50 border-indigo-200',
        icon: <Download className="h-5 w-5" />,
      };
    case 'APPROVE':
      return {
        title: 'Fatura aprovada',
        tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
        icon: <CheckCircle2 className="h-5 w-5" />,
      };
    case 'REJECT':
      return {
        title: 'Fatura reprovada',
        tone: 'text-red-700 bg-red-50 border-red-200',
        icon: <XCircle className="h-5 w-5" />,
      };
    case 'REVERSE':
      return {
        title: 'Fatura estornada',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
        icon: <FileMinus2 className="h-5 w-5" />,
      };
    case 'DELETE':
      return {
        title: 'Fatura excluída',
        tone: 'text-red-700 bg-red-50 border-red-200',
        icon: <Trash2 className="h-5 w-5" />,
      };
    default:
      return {
        title: activity.description,
        tone: 'text-slate-700 bg-slate-50 border-slate-200',
        icon: <Activity className="h-5 w-5" />,
      };
  }
};

const getActivitySummary = (
  activity: ViagemBillingActivity,
  dataInfo: ActivityDataInfo,
  fallbackBilling?: ViagemBilling | null
) => {
  const billingId = dataInfo.viagemBillingId || dataInfo.billingId || fallbackBilling?.id;
  const billingLabel = `Fatura de ID ${billingId || '---'}`;
  const billingShortLabel = `fatura #${billingId || '---'}`;

  if (isRemoveFromBillingActivity(activity, dataInfo)) {
    const totalRemoved = dataInfo.viagemIds?.length;
    if (!totalRemoved) return `Viagem removida da ${billingShortLabel}.`;

    return totalRemoved > 1
      ? `${totalRemoved} viagens removidas da ${billingShortLabel}.`
      : `1 viagem removida da ${billingShortLabel}.`;
  }

  switch (activity.operation) {
    case 'CREATE':
      return `${billingLabel} foi criada.`;
    case 'UPDATE':
      return `${billingLabel} foi atualizada.`;
    case 'EXPORT':
      return `${billingLabel} foi exportada.`;
    case 'APPROVE':
      return `${billingLabel} foi aprovada.`;
    case 'REJECT':
      return `${billingLabel} foi reprovada.`;
    case 'REVERSE':
      return `${billingLabel} foi estornada.`;
    case 'DELETE':
      return `${billingLabel} foi excluída.`;
    default:
      return activity.description;
  }
};

const getRemovedViagensLabel = (dataInfo: ActivityDataInfo) => {
  const viagens = dataInfo.viagens
    ?.map((viagem) => viagem.idViagem || viagem.viagemControleId)
    .filter(Boolean);

  if (viagens?.length) {
    return viagens.join(', ');
  }

  return dataInfo.viagemIds?.length ? dataInfo.viagemIds.join(', ') : undefined;
};

const DetailItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="rounded border border-slate-200 bg-white px-3 py-2">
    <span className="block font-GilroyBold text-[11px] uppercase text-slate-500">
      {label}
    </span>
    <span className="mt-1 block break-words whitespace-pre-wrap font-sans text-sm text-slate-800">
      {value || '---'}
    </span>
  </div>
);

const clearBodyPointerLock = () => {
  [0, 50, 150, 300].forEach((delay) => {
    window.setTimeout(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, delay);
  });
};

export const ViagensBillingActivitiesDialog = ({
  billing,
  onOpenChange,
}: Props) => {
  const { fetchBillingById } = useViagensBilling();
  const open = Boolean(billing);

  const billingQuery = useQuery({
    queryKey: ['viagens-billing-activities', billing?.id],
    queryFn: () => fetchBillingById(billing?.id as number),
    enabled: Boolean(billing?.id),
    refetchOnWindowFocus: false,
  });

  const activities = React.useMemo(() => {
    const billingData = billingQuery.data;
    const activityEntries = (billingData?.activities ?? [])
      .filter((activity) => activity.operation !== 'REJECT')
      .map((activity) => ({ activity, rejection: undefined }));
    const rejectionEntries = (billingData?.rejections ?? []).map((rejection) => ({
      activity: {
        id: rejection.id,
        description: `Fatura ${billingData?.id || '---'} reprovada`,
        operation: 'REJECT' as const,
        table: 'viagem_billings' as const,
        dataInfo: JSON.stringify({
          viagemBillingId: billingData?.id,
          justificationId: rejection.justificationId,
          rejectionId: rejection.id,
        }),
        createdAt: rejection.createdAt,
        user: rejection.user,
      },
      rejection,
    }));

    return [...activityEntries, ...rejectionEntries].sort((a, b) => {
      return new Date(b.activity.createdAt).getTime() - new Date(a.activity.createdAt).getTime();
    });
  }, [billingQuery.data]);

  const currentBilling = billingQuery.data ?? billing;

  const closeDialog = React.useCallback(() => {
    onOpenChange(false);
    clearBodyPointerLock();
  }, [onOpenChange]);

  React.useEffect(() => {
    if (!open) {
      clearBodyPointerLock();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && closeDialog()}>
      <DialogContent className="max-w-[800px] bg-white p-10">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <Activity className="h-6 w-6 text-slate-900" />
          <h2 className="font-GilroyBold text-xl text-slate-900">
            Registro de atividades da fatura - {billing?.id}
          </h2>
        </header>

        {billingQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-slate-500" />
          </div>
        ) : (
          <div className="relative mt-6 max-h-[60vh] overflow-y-auto pl-8 pr-2">
            <div className="absolute bottom-0 left-3 top-0 w-px bg-slate-200" />

            {activities.map(({ activity, rejection }) => {
              const dataInfo = parseDataInfo(activity.dataInfo);
              const presentation = getActivityPresentation(activity, dataInfo);
              const billingId =
                dataInfo.viagemBillingId || dataInfo.billingId || currentBilling?.id;
              const isRemoveActivity = isRemoveFromBillingActivity(activity, dataInfo);
              const removedViagensLabel = getRemovedViagensLabel(dataInfo);
              const removedViagensCount =
                dataInfo.viagens?.length || dataInfo.viagemIds?.length || 0;
              const summary = getActivitySummary(activity, dataInfo, currentBilling);
              const justificationDescription = rejection?.justification?.description;
              const rejectionComment = rejection?.comment;

              return (
                <article key={activity.id} className="relative mb-4">
                  <span className="absolute -left-[31px] top-8 h-5 w-5 rounded-full border-4 border-slate-300 bg-white" />
                  <div className="rounded-md border border-slate-100 bg-slate-50 p-4 shadow-md">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-9 w-9 items-center justify-center rounded-full border ${presentation.tone}`}
                        >
                          {presentation.icon}
                        </span>
                        <div>
                          <strong className="font-GilroyBold text-base text-slate-900">
                            {presentation.title}
                          </strong>
                          <div className="mt-1 flex items-center gap-2 font-sans text-xs text-slate-500">
                            <UserCircle className="h-4 w-4" />
                            {activity.user?.name || 'Usuário não identificado'}
                          </div>
                        </div>
                      </div>
                      <span className="font-GilroyBold text-xs text-slate-600">
                        {formatDate(activity.createdAt)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <DetailItem label="Fatura" value={billingId ? `#${billingId}` : undefined} />
                      {isRemoveActivity && (
                        <DetailItem
                          label={removedViagensCount > 1 ? 'IDs viagens' : 'ID viagem'}
                          value={removedViagensLabel}
                        />
                      )}
                    </div>

                    {(activity.operation === 'REJECT' || activity.operation === 'REVERSE') && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <DetailItem
                          label="Justificativa"
                          value={justificationDescription || dataInfo.justificationDescription}
                        />
                        <DetailItem
                          label="Comentário"
                          value={rejectionComment || dataInfo.comment}
                        />
                      </div>
                    )}

                    <p className="mt-4 rounded border border-slate-200 bg-white px-3 py-2 font-sans text-sm text-slate-700">
                      {summary}
                    </p>
                  </div>
                </article>
              );
            })}

            {!activities.length && (
              <div className="rounded-md bg-slate-50 p-6 text-center font-sans text-sm text-slate-600">
                Nenhuma atividade registrada para esta fatura.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
